import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FormAlert,
  FormCard,
  FormField,
  FormSubmitButton,
  FormTextArea,
  tokens,
} from '../components/forms';
import contactService from '../services/contact.service';
import { getApiErrorMessage } from '../lib/apiError';

const LIMITS = {
  firstName: { min: 1, max: 60 },
  lastName: { min: 1, max: 60 },
  subject: { min: 3, max: 150 },
  message: { min: 10, max: 2000 },
};

export default function Contact(): JSX.Element {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');

  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isValid =
    firstName.trim().length >= LIMITS.firstName.min &&
    lastName.trim().length >= LIMITS.lastName.min &&
    email.trim().length > 0 &&
    subject.trim().length >= LIMITS.subject.min &&
    message.trim().length >= LIMITS.message.min;

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!isValid) return;
    setError(null);
    setLoading(true);
    try {
      await contactService.send({
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: email.trim(),
        subject: subject.trim(),
        message: message.trim(),
      });
      setSubmitted(true);
    } catch (err: unknown) {
      setError(
        getApiErrorMessage(
          err,
          'We could not send your message. Please try again in a few minutes.'
        )
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormCard
      title="Contact us"
      subtitle="Write to us and an Account Executive will get back to you within 24 business hours."
    >
      {submitted ? (
        <>
          <FormAlert variant="success" title="Message sent">
            We received your request. We have sent a confirmation to{' '}
            <span className="font-mono">{email}</span>. Our team will get in
            touch with you shortly.
          </FormAlert>
          <p className="mt-6 text-center text-xs" style={{ color: tokens.textPlaceholder }}>
            <Link to="/login" className="font-medium" style={{ color: tokens.gold }}>
              ← Back to sign in
            </Link>
          </p>
        </>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          {error && <FormAlert variant="error">{error}</FormAlert>}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField
              name="first_name"
              label="First name"
              value={firstName}
              onChange={setFirstName}
              placeholder="Jane"
              autoComplete="given-name"
              maxLength={LIMITS.firstName.max}
              required
              disabled={loading}
            />

            <FormField
              name="last_name"
              label="Last name"
              value={lastName}
              onChange={setLastName}
              placeholder="Doe"
              autoComplete="family-name"
              maxLength={LIMITS.lastName.max}
              required
              disabled={loading}
            />
          </div>

          <FormField
            name="email"
            label="Email"
            type="email"
            value={email}
            onChange={setEmail}
            placeholder="name@company.com"
            autoComplete="email"
            maxLength={254}
            required
            disabled={loading}
          />

          <FormField
            name="subject"
            label="Subject"
            value={subject}
            onChange={setSubject}
            placeholder="Inquiry about our series"
            maxLength={LIMITS.subject.max}
            required
            disabled={loading}
          />

          <FormTextArea
            name="message"
            label="Message"
            value={message}
            onChange={setMessage}
            placeholder="Tell us how we can help you..."
            maxLength={LIMITS.message.max}
            required
            disabled={loading}
          />

          <FormSubmitButton loading={loading} disabled={!isValid}>
            Send message
          </FormSubmitButton>

          <p className="text-center text-xs" style={{ color: tokens.textPlaceholder }}>
            <Link to="/login" className="font-medium" style={{ color: tokens.gold }}>
              ← Back to sign in
            </Link>
          </p>
        </form>
      )}
    </FormCard>
  );
}
